import React, { useRef, useEffect, Fragment } from 'react'

import {
    StyledVideoPlaylist,
    StyledVideoPlaylistItem,
    StyledPlaylistItemImage,
    StyledPlaylistItemDetails,
    StyledPlaylistItemMedia,
    StyledPlaylistItemTitle,
    StyledPlaylistItemPlayIcon,
    StyledRotateRightButton,
    StyledIconChevronRight,
    StyledVideoPlaylistButton,
    StyledPlaylistActions,
    StyledRotateLeftButton,
    StyledIconChevronLeft,
} from './VideoPlaylist.styled'
import { useTheme } from '@emotion/react'
import { getHumanDuration } from '../../utils/get-human-duration'
import { Skeleton } from 'component-library/content/Skeleton/Skeleton'
import { Picture } from 'component-library/content/Picture/Picture'
import { toArray } from 'common/helpers/utils'
import { usePrevious } from 'common/hooks/usePrevious'
import { useScrollInfo } from 'common/hooks/useScrollInfo'
import { isServerEnvironment } from 'common/environment'
import { isLazyLoadGloballyDisabled } from 'component-library/content/Picture/utils'
import { VideoQueueItemMeta } from 'common/events/video-event-types'

export interface VideoPlaylistProps {
    isLoaded?: boolean
    videoQueue: VideoQueueItemMeta[]
    activeItemId: string
    onSelect: (item: VideoQueueItemMeta) => void
    onScroll: () => void
}

export const VideoPlaylist: React.FC<VideoPlaylistProps> = ({
    videoQueue,
    onSelect,
    activeItemId,
    isLoaded,
    onScroll,
}) => {
    const slideWidthPlusPadding = 162
    const [scrollInfo, setRef, slider] = useScrollInfo<HTMLUListElement>()
    const previousItemId = usePrevious(activeItemId)

    // Scroll to active slide
    useEffect(() => {
        const queuePosition = videoQueue.findIndex(item => item.id === activeItemId)

        if (previousItemId !== activeItemId) {
            scrollSlideLeftTo(queuePosition * slideWidthPlusPadding, false, slider)
        }
    }, [activeItemId, previousItemId, videoQueue, slider])

    const handleRotateRight = () => {
        scrollSlideLeftTo(slideWidthPlusPadding, true, slider)
    }

    const handleRotateLeft = () => {
        scrollSlideLeftTo(-slideWidthPlusPadding, true, slider)
    }

    return (
        <Fragment>
            <StyledVideoPlaylist
                aria-roledescription="carousel"
                onScrollCapture={onScroll}
                ref={setRef}
            >
                {videoQueue.map((item, i) => (
                    <VideoPlaylistItem
                        key={i}
                        item={item}
                        onSelect={onSelect}
                        active={activeItemId === item.id}
                        accessibilityLabel={`${i + 1} of ${videoQueue.length} - ${item.name}`}
                    />
                ))}
                {!isLoaded && <StyledVideoPlaylistSkeleton />}
            </StyledVideoPlaylist>

            {isLoaded && (
                <StyledPlaylistActions>
                    <StyledRotateLeftButton
                        type="button"
                        aria-label="Go to the next slide"
                        onClick={handleRotateLeft}
                        visible={scrollInfo.x.percentage !== 0}
                    >
                        <StyledIconChevronLeft />
                    </StyledRotateLeftButton>
                    <StyledRotateRightButton
                        type="button"
                        aria-label="Go to the next slide"
                        onClick={handleRotateRight}
                        visible={scrollInfo.x.percentage !== 1}
                    >
                        <StyledIconChevronRight />
                    </StyledRotateRightButton>
                </StyledPlaylistActions>
            )}
        </Fragment>
    )
}

const supportsNativeSmoothScroll =
    !isServerEnvironment() && 'scrollBehavior' in document.documentElement.style

const scrollSlideLeftTo = (
    position: number,
    relative: boolean = false,
    slider: React.RefObject<HTMLElement>
) => {
    if (!slider.current) {
        return
    }

    if (supportsNativeSmoothScroll) {
        slider.current.scrollTo({
            left: relative ? slider.current.scrollLeft + position : position,
            behavior: 'smooth',
        })
    } else {
        slider.current.scrollLeft = relative ? slider.current.scrollLeft + position : position
    }
}

export const StyledVideoPlaylistSkeleton: React.FC = () => (
    <Fragment>
        {toArray(6).map((_, i) => (
            <StyledVideoPlaylistItem key={i}>
                <Picture layout="placeholder" fixedRatio="16:9" />
                <StyledPlaylistItemTitle>
                    <Skeleton count={2} />
                </StyledPlaylistItemTitle>
            </StyledVideoPlaylistItem>
        ))}
    </Fragment>
)

export interface VideoPlaylistItemProps {
    item: VideoQueueItemMeta
    active: boolean
    onSelect: (item: VideoQueueItemMeta) => void
    accessibilityLabel: string
}

export const VideoPlaylistItem: React.FC<VideoPlaylistItemProps> = ({
    item,
    onSelect,
    active,
    accessibilityLabel,
}) => {
    const { fallbackImages } = useTheme()
    const ref = useRef<HTMLLIElement>(null)

    const handleClick = () => onSelect(item)

    const imageWithFallbacks = item.posterImage || fallbackImages.crops['16:9']

    return (
        <StyledVideoPlaylistItem
            ref={ref}
            aria-roledescription="slide"
            aria-label={accessibilityLabel}
            aria-current={active}
        >
            <StyledVideoPlaylistButton
                active={active}
                title={`Click to play ${item.name}`}
                onClick={handleClick}
            >
                <StyledPlaylistItemMedia>
                    <StyledPlaylistItemImage
                        src={imageWithFallbacks.reference}
                        enableFadeIn={!isLazyLoadGloballyDisabled()}
                        alt={item.name || item.description}
                    />
                    <StyledPlaylistItemDetails>
                        <StyledPlaylistItemPlayIcon />
                        {item.videoType !== 'live' ? getHumanDuration(item.duration) : null}
                    </StyledPlaylistItemDetails>
                </StyledPlaylistItemMedia>
                <StyledPlaylistItemTitle>{item.name}</StyledPlaylistItemTitle>
            </StyledVideoPlaylistButton>
        </StyledVideoPlaylistItem>
    )
}
